import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../services/api'

export const fetchEntityLists = createAsyncThunk('entityList/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try { return await api.get('/api/v1/entity-lists', { params }).then(r => r.data) }
    catch (e) { return rejectWithValue(e.response?.data?.detail || e.message) }
  }
)

export const fetchEntityListStats = createAsyncThunk('entityList/fetchStats',
  async (_, { rejectWithValue }) => {
    try { return await api.get('/api/v1/entity-lists/stats').then(r => r.data) }
    catch (e) { return rejectWithValue(e.response?.data?.detail || e.message) }
  }
)

export const createEntityListEntry = createAsyncThunk('entityList/create',
  async (payload, { rejectWithValue }) => {
    try { return await api.post('/api/v1/entity-lists', payload).then(r => r.data) }
    catch (e) { return rejectWithValue(e.response?.data?.detail || e.message) }
  }
)

export const updateEntityListEntry = createAsyncThunk('entityList/update',
  async ({ id, payload }, { rejectWithValue }) => {
    try { return await api.patch(`/api/v1/entity-lists/${id}`, payload).then(r => r.data) }
    catch (e) { return rejectWithValue(e.response?.data?.detail || e.message) }
  }
)

export const deleteEntityListEntry = createAsyncThunk('entityList/delete',
  async (id, { rejectWithValue }) => {
    try { await api.delete(`/api/v1/entity-lists/${id}`); return id }
    catch (e) { return rejectWithValue(e.response?.data?.detail || e.message) }
  }
)

export const bulkCreateEntityListEntries = createAsyncThunk('entityList/bulkCreate',
  async (entries, { rejectWithValue }) => {
    try { return await api.post('/api/v1/entity-lists/bulk', { entries }).then(r => r.data) }
    catch (e) { return rejectWithValue(e.response?.data?.detail || e.message) }
  }
)

export const refreshEntityListCache = createAsyncThunk('entityList/refreshCache',
  async (_, { rejectWithValue }) => {
    try { return await api.post('/api/v1/entity-lists/refresh-cache').then(r => r.data) }
    catch (e) { return rejectWithValue(e.response?.data?.detail || e.message) }
  }
)

const entityListSlice = createSlice({
  name: 'entityList',
  initialState: {
    entries:  [],
    stats:    null,
    total:    0,
    loading:  false,
    saving:   false,
    error:    null,
    filters:  { search: '', list_type: '', entity_type: '', is_active: 'true' },
    pagination: { page: 1, limit: 20 },
  },
  reducers: {
    setFilter(state, { payload }) {
      state.filters = { ...state.filters, ...payload }
      state.pagination.page = 1
    },
    resetFilters(state) {
      state.filters = { search: '', list_type: '', entity_type: '', is_active: 'true' }
      state.pagination.page = 1
    },
    setPage(state, { payload }) {
      state.pagination.page = payload
    },
    clearError(state) { state.error = null },
  },
  extraReducers: (b) => {
    b.addCase(fetchEntityLists.pending,   s =>        { s.loading = true;  s.error = null })
    b.addCase(fetchEntityLists.rejected,  (s, a) =>   { s.loading = false; s.error = a.payload })
    b.addCase(fetchEntityLists.fulfilled, (s, a) =>   { s.loading = false; s.entries = a.payload.data ?? []; s.total = a.payload.total ?? 0 })

    b.addCase(fetchEntityListStats.fulfilled, (s, a) => { s.stats = a.payload })

    b.addCase(createEntityListEntry.pending,   s =>      { s.saving = true;  s.error = null })
    b.addCase(createEntityListEntry.rejected,  (s, a) => { s.saving = false; s.error = a.payload })
    b.addCase(createEntityListEntry.fulfilled, (s, a) => { s.saving = false; s.entries = [a.payload, ...s.entries]; s.total += 1 })

    b.addCase(updateEntityListEntry.pending,   s =>      { s.saving = true;  s.error = null })
    b.addCase(updateEntityListEntry.rejected,  (s, a) => { s.saving = false; s.error = a.payload })
    b.addCase(updateEntityListEntry.fulfilled, (s, a) => {
      s.saving = false
      const idx = s.entries.findIndex(e => e.id === a.payload.id)
      if (idx !== -1) s.entries[idx] = { ...s.entries[idx], ...a.payload }
    })

    b.addCase(deleteEntityListEntry.pending,   s =>      { s.saving = true })
    b.addCase(deleteEntityListEntry.rejected,  (s, a) => { s.saving = false; s.error = a.payload })
    b.addCase(deleteEntityListEntry.fulfilled, (s, a) => { s.saving = false; s.entries = s.entries.filter(e => e.id !== a.payload); s.total = Math.max(0, s.total - 1) })

    b.addCase(bulkCreateEntityListEntries.pending,   s =>      { s.saving = true;  s.error = null })
    b.addCase(bulkCreateEntityListEntries.rejected,  (s, a) => { s.saving = false; s.error = a.payload })
    b.addCase(bulkCreateEntityListEntries.fulfilled, s =>      { s.saving = false })

    b.addCase(refreshEntityListCache.rejected, (s, a) => { s.error = a.payload })
  },
})

export const { setFilter, resetFilters, setPage, clearError } = entityListSlice.actions

export const selectEntries    = s => s.entityList.entries
export const selectStats      = s => s.entityList.stats
export const selectTotal      = s => s.entityList.total
export const selectLoading    = s => s.entityList.loading
export const selectSaving     = s => s.entityList.saving
export const selectError      = s => s.entityList.error
export const selectFilters    = s => s.entityList.filters
export const selectPagination = s => s.entityList.pagination

export default entityListSlice.reducer